'use client'

import { IconBookmark, IconCheck } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { LoginDialog } from '@/components/auth/login-dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { analytics } from '@/lib/analytics'
import { isWordSaved } from '@/lib/study'
import {
  saveDictionaryWordToDefaultDeck,
  type StudyWordEntry
} from '@/lib/save-to-study'
import { completeOnboardingActivationForCurrentStory } from '@/lib/onboarding'

export function SaveStudyWordButton({
  entry,
  source,
  className,
  label = 'Save word',
  savedLabel = 'Saved',
  variant = 'outline',
  size = 'default',
  iconOnly = false,
  analyticsSource
}: {
  entry: StudyWordEntry
  source?: Record<string, unknown>
  className?: string
  label?: string
  savedLabel?: string
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'default' | 'sm' | 'lg' | 'icon' | 'icon-sm' | 'icon-lg'
  iconOnly?: boolean
  analyticsSource?: string
}) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { data: saved = false, isLoading } = useQuery({
    queryKey: ['bookmark', entry.id, user?.id],
    queryFn: () => {
      if (!user?.id) return false
      return isWordSaved(entry.id)
    },
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5
  })

  const mutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not logged in')
      return saveDictionaryWordToDefaultDeck(user.id, entry, source)
    },
    onSuccess: () => {
      analytics.itemBookmarked(entry.id, true)
      if (analyticsSource) analytics.readerWordSaved(entry.id, analyticsSource)
      const onboardingState = completeOnboardingActivationForCurrentStory()
      if (onboardingState) {
        analytics.onboardingCompleted(onboardingState.recommendedStorySlug)
      }
      toast.success('Saved to Saved Words')
      queryClient.setQueryData(['bookmark', entry.id, user?.id], true)
      queryClient.invalidateQueries({ queryKey: ['bulk-bookmarks'] })
      queryClient.invalidateQueries({
        queryKey: ['word-familiarity', user?.id, entry.id]
      })
      queryClient.invalidateQueries({
        queryKey: ['content-familiarity', user?.id]
      })
      queryClient.invalidateQueries({
        queryKey: ['path-story-readiness', user?.id]
      })
      queryClient.invalidateQueries({ queryKey: ['story-recommendations'] })
      queryClient.invalidateQueries({ queryKey: ['all-collections', user?.id] })
      queryClient.invalidateQueries({
        queryKey: ['reader-study-saved-words', user?.id]
      })
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Could not save word'
      )
    }
  })

  const content = (
    <>
      {saved ? (
        <IconCheck className="h-4 w-4" />
      ) : (
        <IconBookmark className="h-4 w-4" />
      )}
      {!iconOnly &&
        (mutation.isPending ? 'Saving...' : saved ? savedLabel : label)}
    </>
  )

  if (!user?.id) {
    return (
      <LoginDialog>
        <Button
          className={className}
          variant={variant}
          size={size}
          aria-label={label}
        >
          <IconBookmark className="h-4 w-4" />
          {!iconOnly && label}
        </Button>
      </LoginDialog>
    )
  }

  return (
    <Button
      className={className}
      variant={saved ? 'outline' : variant}
      size={size}
      disabled={isLoading || mutation.isPending || saved}
      aria-label={saved ? savedLabel : label}
      onClick={() => mutation.mutate()}
    >
      {content}
    </Button>
  )
}
